import SpeakBtn from './SpeakBtn';
import { AquaCycle } from './AquaCycle';
import { secText as speakText, stopTts } from '../lib/helpAudio';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, BookOpen, Waves, CloudRain, Download, Loader2, Lightbulb, Search, ListTree, Rocket, ShoppingBasket, TreePine } from 'lucide-react';
import TREE from '../lib/featureTree.json';
import { openTree } from './FeatureTree';
import SymbolLegend from './SymbolLegend';
import { tl } from '../lib/terms';
import { toast } from 'sonner';
import { modeId, openProfiles } from '../lib/profiles';
import { resetSetup } from './SetupGuide';
import { mtx } from '../lib/manualTopics';
import { buildManual, buildAquaManual, buildStormManual, chapterSecs } from '../lib/manualRef';
import { manualPdf, manualMd } from '../lib/manualPdf';
import { I18N } from '../lib/i18n';
import { restartTour } from './TourGuide';

const norm = (s) => String(s || '').toLowerCase();
const secText = (s) => norm([s.title, ...s.steps, s.tip].join(' '));
// tolerant search: word stems, compound words ("leckmengenmessung") and field synonyms
const SYN = { leckmenge: 'torricelli', leckrate: 'torricelli', wasserverlust: 'torricelli', verlustmenge: 'torricelli', leakage: 'torricelli', perdita: 'torricelli', fuga: 'torricelli', fuite: 'torricelli', abstecken: 'absteck', stakeout: 'stake', logger: 'logger', handschuh: 'handschuh', glove: 'glove' };
const stem = (w) => (w.length > 6 ? w.slice(0, Math.max(5, w.length - 3)) : w);
const parts = (w) => {
  const out = [w];
  Object.keys(SYN).forEach((k) => { if (w.includes(stem(k))) out.push(SYN[k]); });
  for (let i = 4; i <= w.length - 4; i += 1) out.push(`${w.slice(0, i)}|${w.slice(i)}`);
  return out;
};
const hasWord = (txt, w) => parts(w).some((p) => (p.includes('|') ? p.split('|').every((x) => x.length > 3 && txt.includes(stem(x))) : txt.includes(stem(p))));

function Hl({ text, words }) {
  if (!words.length || !text) return text || null;
  const re = new RegExp(`(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  return String(text).split(re).map((p, i) => (i % 2 ? <mark key={i}>{p}</mark> : p));
}

const T = (l) => I18N[l] || I18N.de;
function Section({ s, words, open, lang, tip, focus, onAq }) {
  return (
    <details className={`man-sec ${focus === s.key ? 'man-focus' : ''}`} open={open || focus === s.key} data-testid={`manual-sec-${s.id}`}>
      <summary><span className="man-no">{s.id}</span> <Hl text={s.title} words={words} /><SpeakBtn t={T(lang)} id={s.key} lang={lang} text={speakText(s)} testid={`speak-${s.id}`} /></summary>
      <div className={s.img ? 'manual-grid' : ''}>
        {s.imgs && <div className="man-shots" data-testid={`manual-shots-${s.id}`}>{s.imgs.map((im) => <img key={im} src={`/manual/${lang}/${im}.jpg`} alt={s.title} loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />)}</div>}
        <div>
          <ol>{s.steps.map((st) => <li key={st}><Hl text={st} words={words} /></li>)}</ol>
          {s.tip && <p className="manual-tip"><Lightbulb size={14} /> <b>{tip}:</b> <Hl text={s.tip} words={words} /></p>}
          {s.aq && <button type="button" className="man-aqref" data-testid={`manual-aqref-${s.id}`} onClick={() => onAq(s.aq.n)}><Waves size={14} /> {s.aq.title}</button>}
        </div>
        {s.img && <figure><img src={`/manual/${lang}/${s.img}.jpg`} alt={s.title} loading="lazy" /></figure>}
      </div>
    </details>
  );
}

function Toc({ ui, chapters, go, active }) {
  return (
    <nav className="man-toc" data-testid="manual-toc">
      <b><ListTree size={15} /> {ui.toc}</b>
      <ol>{chapters.map((c) => (
        <li key={c.n}><button type="button" className={active === c.n ? 'on' : ''} data-testid={`manual-toc-${c.n}`} onClick={() => go(c.n)}>
          <span className="man-no">{c.n}</span><span>{c.icon} {c.title}</span><small>{c.count}</small>
        </button></li>
      ))}</ol>
    </nav>
  );
}

// exhaustive 15-chapter reference manual with table of contents + full-text search
export default function ManualDialog({ lang, focus, onClose }) {
  useEffect(() => () => stopTts(), []);
  useEffect(() => { const k = (e) => e.key === 'Escape' && onClose(); window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  useEffect(() => { const b = document.body.style, h = document.documentElement.style, o = [b.overflow, h.overflow]; b.overflow = 'hidden'; h.overflow = 'hidden'; return () => { [b.overflow, h.overflow] = o; }; }, []);
  const [book, setBook] = useState(String(focus || '').startsWith('aq:') ? 'aqua' : String(focus || '').startsWith('sw:') ? 'storm' : 'main');
  const aqb = useMemo(() => buildAquaManual(lang), [lang]);
  const swb = useMemo(() => buildStormManual(lang), [lang]);
  const main = useMemo(() => buildManual(lang), [lang]);
  const isAq = book === 'aqua', isSw = book === 'storm', ui = main.ui;
  const m = isAq ? { ...main.m, title: aqb.a.title, intro: aqb.a.intro, quick: null } : isSw ? { ...main.m, title: swb.a.title, intro: swb.a.intro, quick: null } : main.m;
  const chapters = isAq ? aqb.chapters : isSw ? swb.chapters : main.chapters;
  const pickBook = (b) => { setBook(b); setQ(''); bodyRef.current?.scrollTo(0, 0); };
  const [jump, setJump] = useState(() => +String(focus || '').split('aq:')[1] || null);
  const toAq = (n) => { setBook('aqua'); setJump(n); };
  useEffect(() => { if (jump && isAq) { setTimeout(() => go(jump), 80); setJump(null); } }, [jump, isAq]); // eslint-disable-line react-hooks/exhaustive-deps
  const x = mtx(lang), cur = modeId(), mode = cur.split('_')[0];
  const [only, setOnly] = useState(false);
  const [q, setQ] = useState('');
  const [openAll, setOpenAll] = useState(false);
  const [sym, setSym] = useState(false);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);
  const words = q.trim().length > 1 ? norm(q).split(/\s+/).filter((w) => w.length > 1) : [];
  const match = (s) => !words.length || words.every((w) => hasWord(secText(s), w));
  const view = chapters.map((c) => {
    const subs = c.subs?.filter((s) => !only || mode === 'all' || s.mode === cur || s.mode.startsWith(`${mode}_`) || s.mode === mode).map((s) => ({ ...s, secs: s.secs.filter(match) })).filter((s) => s.secs.length);
    const secs = c.secs.filter(match);
    return { ...c, secs, subs, count: secs.length + (subs || []).reduce((a, s) => a + s.secs.length, 0) };
  }).filter((c) => c.count);
  const hits = view.reduce((a, c) => a + c.count, 0);
  const go = (n) => { setActive(n); bodyRef.current?.querySelector(`#man-ch-${n}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  const save = async () => { setBusy(true); try { await manualPdf(lang, book); } catch { toast.error('PDF'); } setBusy(false); };
  const open = openAll || words.length > 0;
  const focusId = useMemo(() => chapters.flatMap((c) => chapterSecs(c)).find((s) => s.key === focus)?.id, [chapters, focus]);
  useEffect(() => { if (focusId) setTimeout(() => bodyRef.current?.querySelector(`[data-testid="manual-sec-${focusId}"]`)?.scrollIntoView({ block: 'start' }), 120); }, [focusId]);
  return createPortal(
    <div className="editor-backdrop man-backdrop" data-testid="manual-dialog" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="editor manual man2">
        <div className="editor-head">
          <h4 data-testid="manual-title">{isAq ? <Waves size={18} /> : isSw ? <CloudRain size={18} /> : <BookOpen size={18} />} {m.title}</h4>
          <button className="btn btn-outline btn-sm" data-testid="manual-close-btn" onClick={onClose} aria-label="close"><X size={16} /></button>
        </div>
        <div className="man-bar">
          <label className="man-search"><Search size={16} /><input data-testid="manual-search-input" value={q} placeholder={ui.search} onChange={(e) => setQ(e.target.value)} />{q && <button type="button" className="icon-btn" data-testid="manual-search-clear" onClick={() => setQ('')}><X size={14} /></button>}</label>
          {words.length > 0 && <small data-testid="manual-search-hits" className={hits ? '' : 'man-none'}>{hits ? ui.hits.replace('{n}', hits) : ui.none}</small>}
          <label className={`manual-filter ${only ? 'on' : ''}`} data-testid="manual-mode-filter"><input type="checkbox" data-testid="manual-mode-filter-check" checked={only} onChange={(e) => setOnly(e.target.checked)} /> {x.mhFilter}</label>
          <button className="btn btn-outline btn-sm" data-testid="manual-expand-btn" onClick={() => setOpenAll(!openAll)}>{openAll ? ui.collapse : ui.expand}</button>
          <button className="btn btn-primary btn-sm" data-testid="manual-starthilfe-btn" onClick={() => { onClose(); resetSetup(); restartTour(); toast.success(I18N[lang]?.startHelpDone); }}><Rocket size={15} /> {I18N[lang]?.startHelp}</button>
          <button className="btn btn-outline btn-sm" data-testid="manual-tree-btn" onClick={() => { onClose(); openTree(); }}><TreePine size={16} /> {TREE[lang]?.ui.title || TREE.de.ui.title}</button>
          <button className="btn btn-outline btn-sm" data-testid="manual-symbols-btn" onClick={() => setSym(true)}><ShoppingBasket size={16} /> {I18N[lang]?.slBasket}</button>
          <button className="btn btn-outline btn-sm" data-testid="manual-tour-btn" onClick={() => { onClose(); restartTour(); }}>{I18N[lang]?.tourRestart}</button>
          {book !== 'main' && <button className="btn btn-sm btn-outline" data-testid="manual-book-main" onClick={() => pickBook('main')}><BookOpen size={16} /> {aqb.a.ui.back}</button>}
          {!isAq && <button className="btn btn-sm btn-aq" data-testid="manual-book-toggle" onClick={() => pickBook('aqua')}><Waves size={16} /> {aqb.a.ui.open}</button>}
          {!isSw && <button className="btn btn-sm btn-aq" data-testid="manual-book-storm" onClick={() => pickBook('storm')}><CloudRain size={16} /> {swb.a.ui.open}</button>}
          <button className="btn btn-proj btn-sm" data-testid="manual-pdf-btn" disabled={busy} onClick={save}>{busy ? <Loader2 size={16} className="spin" /> : <Download size={16} />} {m.save}</button>
          <button className="btn btn-outline btn-sm" data-testid="manual-md-btn" onClick={() => manualMd(lang, book)}><Download size={16} /> Markdown</button>
        </div>
        <div className="man-cols">
          <Toc ui={ui} chapters={view} go={go} active={active} />
          <div className="manual-body man-main" ref={bodyRef}>
            {!words.length && <p className="manual-intro">{m.intro}</p>}
            {!words.length && isAq && <AquaCycle ui={aqb.a.ui} />}
            {!words.length && m.quick && <div className="manual-quick" data-testid="manual-quickstart"><b>{m.quick.title}</b><SpeakBtn t={T(lang)} id="quick" lang={lang} text={`${m.quick.title}. ${m.quick.steps.join('. ')}`} testid="speak-quick" /><ul>{m.quick.steps.map((s) => <li key={s}>{s}</li>)}</ul></div>}
            {view.map((c) => (
              <section key={c.n} id={`man-ch-${c.n}`} className="man-ch" data-testid={`manual-ch-${c.n}`}>
                <h3><span className="man-no big">{c.n}</span> {c.icon} {c.title}</h3>
                {c.secs.map((s) => <Section key={s.id} s={s} words={words} open={open} lang={lang} tip={m.tip} focus={focus} onAq={toAq} />)}
                {c.subs?.map((sb) => (
                  <div key={sb.k} className="man-sub" data-testid={`manual-sub-${sb.k}`}>
                    <h4>{sb.title}</h4>
                    {sb.secs.map((s) => <Section key={s.id} s={s} words={words} open={open} lang={lang} tip={m.tip} focus={focus} onAq={toAq} />)}
                  </div>
                ))}
              </section>
            ))}
          </div>
        </div>
      </div>
      {sym && <SymbolLegend t={tl(lang)} onClose={() => setSym(false)} />}
    </div>,
    document.body,
  );
}
